import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';

import lang from '../../../../lib/language';
import { PlanPhaseTypeEnum } from '../../../../lib/models/crm/types';
import { RootState } from '../../../../lib/store';
import { getCurrentAccountTradingPlan } from '../../../../lib/store/crm/helpers';

import { Questionmark, ResetDailyLossConfirmModal } from '../../../../components';
import useIsMobile from '../../../../shared_components/hooks/useIsMobile';
import PieChart from '../PieChart/PieChart';

import { formatNumber, getLabelTextAndStyle } from './additionalInfo.helpers';
import ResetDailyLossButton from './ResetDailyLossButton';

import styles from './AdditionalInfo.module.scss';

const AdditionalInfo = (props: ReduxProps) => {
  const {
    tradingPlan,
    accountStatusData,
  } = props;
  const { isMobile, isTablet } = useIsMobile();
  const { dailyLoss, maxLoss, dailyResetProcessing, totalLossReached, dailyLossReached } = accountStatusData;
  const isDemo = tradingPlan?.phase === PlanPhaseTypeEnum.demo;
  const isChallengePhase = tradingPlan?.phase === PlanPhaseTypeEnum.challenge;
  const isResetButtonDisabled = !tradingPlan || isDemo || dailyResetProcessing || totalLossReached;

  const [showDailyLossResetConfirmModal, setShowDailyLossResetConfirmModal] = useState<boolean>(false);
  const [isDailyLossContent, setIsDailyLossContent] = useState<boolean>(false);

  const maximumTotalLossAmount = tradingPlan?.maximum_total_loss_amount ?? 0;
  const dailyLossLimitAmount = tradingPlan?.daily_loss_limit_amount ?? 0;

  const formatToPositiveMaxLoss = Math.max(0, Math.abs(Number(maxLoss.toFixed(2))));
  const formatToPositiveDailyLoss = Math.max(0, Math.abs(Number(dailyLoss.toFixed(2))));

  const maxLossLabelProps = getLabelTextAndStyle(formatToPositiveMaxLoss, maximumTotalLossAmount);
  const dailyLossLabelProps = getLabelTextAndStyle(formatToPositiveDailyLoss, dailyLossLimitAmount);

  return (
    <div className={classNames(
      styles.container,
      { [styles.nonChallengePhaseContainer]: ((!isChallengePhase || dailyLossReached) && !isMobile && !isTablet) },
    )}
    >
      <div className={styles.pies}>
        <div className={styles.pie}>
          <div className={styles.header}>
            <div className={styles.title}>{lang.maxDrawdown()}</div>
            <Questionmark text={lang.maxDrawdownDescription()} title={lang.maxDrawdown()} tooltipUp />
          </div>
          <div className={styles.chart}>
            <div className={styles.label} style={{ ...maxLossLabelProps.style }}>{maxLossLabelProps.text}</div>
            <PieChart
              value={formatToPositiveMaxLoss}
              maxValue={maximumTotalLossAmount}
              size={112}
            />
          </div>
          <div className={styles.footer}>
            <div className={styles.price}>
              {formatNumber(maxLoss)}
            </div>
            <span className={styles.label}>
              {`/ ${formatNumber(maximumTotalLossAmount)}`}
            </span>
          </div>
        </div>
        <div className={styles.pie}>
          <div className={styles.header}>
            <div className={styles.title}>{lang.dailyLossLimit()}</div>
            <Questionmark
              text={lang.dailyLossDescription()}
              title={lang.dailyLossLimit()}
              tooltipUp
              tooltipTopPosition={-116}
            />
          </div>
          <div className={styles.chart}>
            <div className={styles.label} style={{ ...dailyLossLabelProps.style }}>{dailyLossLabelProps.text}</div>
            <PieChart
              value={!dailyResetProcessing ? formatToPositiveDailyLoss : 0}
              maxValue={dailyLossLimitAmount}
              size={112}
            />
          </div>
          <div className={styles.footer}>
            <div className={styles.price}>
              {!dailyResetProcessing ? formatNumber(dailyLoss) : 0}
            </div>
            <span className={styles.label}>
              {`/ ${formatNumber(dailyLossLimitAmount)}`}
            </span>
          </div>
        </div>
      </div>
      {(isChallengePhase && !dailyLossReached) && (
        <ResetDailyLossButton
          disabled={isResetButtonDisabled}
          onClick={() => setShowDailyLossResetConfirmModal(true)}
        />
      )}
      {showDailyLossResetConfirmModal && (
        <ResetDailyLossConfirmModal
          isOpen
          isComingFromRewindButtonClick
          isDailyLossReached={isDailyLossContent}
          toggleModal={() => {
            setShowDailyLossResetConfirmModal(false);
            setIsDailyLossContent(false);
          }}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  tradingPlan: getCurrentAccountTradingPlan(state.crm),
  accountStatusData: state.closeout.accountStatusData,
});

const connector = connect(mapStateToProps, () => ({}));
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(AdditionalInfo);
